import { Touchable, UserAvatar } from "@kalyzee/kast-web-sdk/web-components";
import { useMeetingSelector } from "@kalyzee/kast-web-sdk";
import clsx from "clsx";
import React, { useMemo, useState } from "react";
import { useMeetingUtilsContext } from "../../contexts/meetingUtils/context";
import { PopulatedUser } from "../../contexts/meetingUtils/interfaces";
import { isExpert } from "../../helpers/roles";
import { getNormalUser } from "../../helpers/user";
import { UserCardAvatarArea } from "./UserCardAvatarArea";

import styles from "./ParticipantsPopover.module.css";

export interface ParticipantsPopoverProps {
  className?: string;
}

/** Connected first, then alphabetically — the list is read top-down. */
const sortUsers = (users: PopulatedUser[]) =>
  [...users]
    .map(pUser => ({ pUser, ...getNormalUser(pUser.user) }))
    .sort((a, b) => {
      if (a.pUser.active !== b.pUser.active) return a.pUser.active ? -1 : 1;
      return a.displayName.localeCompare(b.displayName);
    });

/**
 * Participant list hanging under the watchers badge. Renders nothing outside a
 * meeting, where there is no participant to list.
 */
export const ParticipantsPopover = React.memo(({ className }: ParticipantsPopoverProps) => {
  const meetingCtx = useMeetingUtilsContext({ raiseIfUndefined: false });
  const [ownSession] = useMeetingSelector(meetingCtx?.containerAPI, "session");
  const [showAll, setShowAll] = useState(false);

  // Only moderators may look past the watchers.
  const canSeeAll = isExpert(ownSession);

  // Watchers are what the eye counts: sessions that are not expert ones.
  const { watchers, others } = useMemo(() => {
    const users = meetingCtx?.populated.users ?? [];
    const isWatcher = (pUser: PopulatedUser) => pUser.sessions.some(s => !isExpert(s.session));
    return {
      watchers: sortUsers(users.filter(isWatcher)),
      others: sortUsers(users.filter(u => !isWatcher(u))),
    };
  }, [meetingCtx?.populated.users]);

  if (!meetingCtx) return null;

  const renderRow = ({ pUser, displayName, avatar }: ReturnType<typeof sortUsers>[number]) => (
    <div key={pUser.id} className={styles.row}>
      <UserCardAvatarArea user={{ id: pUser.id, username: displayName, avatar }}>
        <UserAvatar
          className={styles.avatar}
          mode="image"
          image={avatar}
          username={displayName}
        />
      </UserCardAvatarArea>
      <span className={clsx(styles.name, { [styles.nameOffline]: !pUser.active })}>
        {displayName}
      </span>
      <span className={clsx(styles.statusDot, { [styles.statusDotOffline]: !pUser.active })} />
    </div>
  );

  return (
    <div className={clsx(styles.container, className)}>
      <div className={styles.header}>Participants ({watchers.length})</div>
      <div className={styles.list}>
        {watchers.map(renderRow)}
        {!watchers.length && <div className={styles.empty}>Aucun participant</div>}
        {showAll && !!others.length && (
          <>
            <div className={styles.separator}>Autres ({others.length})</div>
            {others.map(renderRow)}
          </>
        )}
      </div>
      {canSeeAll && !!others.length && (
        <Touchable className={styles.toggle} onPress={() => setShowAll(value => !value)}>
          {showAll ? "Cacher" : "Tous les afficher"}
        </Touchable>
      )}
    </div>
  );
});
